import { useEffect, useState } from "react";

import { formatChinaDateTime } from "../time";
import {
  configurePersonalLawyerProduct,
  adjustPersonalLawyerBalance,
  disablePersonalLawyer,
  enablePersonalLawyer,
  getPersonalLawyer,
  listPersonalLawyers,
  type InstitutionProductStatus,
  type LawyerVerificationStatus,
  type PersonalLawyerAccountStatus,
  type PersonalLawyerAdminDetail,
  type PersonalLawyerAdminListItem,
} from "./api";

const PAGE_SIZE = 10;

const VERIFY_LABEL: Record<LawyerVerificationStatus, string> = {
  NOT_SUBMITTED: "未提交",
  PENDING: "待审核",
  APPROVED: "已认证",
  REJECTED: "已驳回",
};

type StatusConfirmation = { enabled: boolean; reason: string };
type ProductDraft = { status: InstitutionProductStatus; price: string };


export function PersonalLawyers() {
  const [items, setItems] = useState<PersonalLawyerAdminListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [draftQuery, setDraftQuery] = useState("");
  const [query, setQuery] = useState("");
  const [verificationStatus, setVerificationStatus] = useState<LawyerVerificationStatus | "">("");
  const [accountStatus, setAccountStatus] = useState<PersonalLawyerAccountStatus | "">("");
  const [detail, setDetail] = useState<PersonalLawyerAdminDetail | null>(null);
  const [productDrafts, setProductDrafts] = useState<Record<string, ProductDraft>>({});
  const [confirmation, setConfirmation] = useState<StatusConfirmation | null>(null);
  const [balanceDraft, setBalanceDraft] = useState<{ bucket: "RECHARGE" | "REWARD"; amount: string; reason: string }>({ bucket: "RECHARGE", amount: "", reason: "" });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function reload() {
    setLoading(true);
    setError("");
    try {
      const result = await listPersonalLawyers({
        query,
        verificationStatus,
        accountStatus,
        page,
        pageSize: PAGE_SIZE,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "个人律师列表加载失败");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, [page, query, verificationStatus, accountStatus]);

  function useDetail(next: PersonalLawyerAdminDetail) {
    setDetail(next);
    setProductDrafts(Object.fromEntries(next.products.map((product) => [
      product.product_code,
      { status: product.status, price: product.personal_price ?? "" },
    ])));
  }

  async function openDetail(id: string) {
    setError("");
    try {
      useDetail(await getPersonalLawyer(id));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "个人律师详情加载失败");
    }
  }

  async function changeStatus() {
    if (!detail || !confirmation?.reason.trim() || submitting) return;
    setSubmitting(true);
    try {
      const next = confirmation.enabled
        ? await enablePersonalLawyer(detail.id, confirmation.reason.trim())
        : await disablePersonalLawyer(detail.id, confirmation.reason.trim());
      useDetail(next);
      setConfirmation(null);
      await reload();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "账号状态修改失败");
    } finally {
      setSubmitting(false);
    }
  }

  async function saveProduct(productCode: string) {
    if (!detail || submitting) return;
    const draft = productDrafts[productCode];
    if (!draft) return;
    setSubmitting(true);
    try {
      await configurePersonalLawyerProduct(detail.id, productCode, {
        status: draft.status,
        personal_price: draft.price.trim() || null,
      });
      useDetail(await getPersonalLawyer(detail.id));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "产品配置保存失败");
    } finally {
      setSubmitting(false);
    }
  }

  async function adjustBalance() {
    if (!detail || !balanceDraft.amount.trim() || !balanceDraft.reason.trim() || submitting) return;
    setSubmitting(true);
    try {
      await adjustPersonalLawyerBalance(detail.id, {
        bucket: balanceDraft.bucket,
        amount_delta: balanceDraft.amount.trim(),
        reason: balanceDraft.reason.trim(),
      });
      useDetail(await getPersonalLawyer(detail.id));
      setBalanceDraft({ bucket: "RECHARGE", amount: "", reason: "" });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "余额调整失败");
    } finally {
      setSubmitting(false);
    }
  }

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return <section className="platform-module platform-personal-lawyers">
    <form className="platform-list-toolbar" onSubmit={(event) => {
      event.preventDefault();
      setPage(1);
      setQuery(draftQuery.trim());
    }}>
      <input className="platform-list-search" value={draftQuery} onChange={(event) => setDraftQuery(event.target.value)} placeholder="搜索律师姓名、手机号或律师编号" aria-label="搜索个人律师" />
      <select value={verificationStatus} aria-label="实名认证状态" onChange={(event) => { setPage(1); setVerificationStatus(event.target.value as LawyerVerificationStatus | ""); }}>
        <option value="">全部认证状态</option><option value="NOT_SUBMITTED">未提交</option><option value="PENDING">待审核</option><option value="APPROVED">已认证</option><option value="REJECTED">已驳回</option>
      </select>
      <select value={accountStatus} aria-label="账号状态" onChange={(event) => { setPage(1); setAccountStatus(event.target.value as PersonalLawyerAccountStatus | ""); }}>
        <option value="">全部账号状态</option><option value="ACTIVE">正常</option><option value="DISABLED">已停用</option>
      </select>
      <button className="platform-primary-button" type="submit">查询</button>
      <button type="button" onClick={() => { setDraftQuery(""); setQuery(""); setVerificationStatus(""); setAccountStatus(""); setPage(1); }}>重置</button>
    </form>
    {error && <p className="form-error" role="alert">{error}</p>}
    <div className="platform-table-wrap"><table className="platform-account-table"><thead><tr>
      <th>律师</th><th>手机号</th><th>所在律所</th><th>实名认证</th><th>账号状态</th><th>注册时间</th><th>操作</th>
    </tr></thead><tbody>
      {loading && <tr><td colSpan={7} className="platform-table-state">正在加载…</td></tr>}
      {!loading && items.length === 0 && <tr><td colSpan={7} className="platform-table-state">暂无符合条件的个人律师</td></tr>}
      {!loading && items.map((item) => <tr key={item.id}>
        <td><strong>{item.lawyer_name}</strong><small>{item.lawyer_number}</small></td><td>{item.mobile}</td><td>{item.law_firm_name ?? "—"}</td>
        <td><span className={`platform-status is-${item.verification_status.toLowerCase()}`}>{VERIFY_LABEL[item.verification_status]}</span></td>
        <td><span className={`platform-status is-${item.account_status.toLowerCase()}`}>{item.account_status === "ACTIVE" ? "正常" : "已停用"}</span></td>
        <td>{formatChinaDateTime(item.registered_at)}</td><td><button className="platform-link-button" type="button" onClick={() => void openDetail(item.id)}>查看管理</button></td>
      </tr>)}
    </tbody></table></div>
    <footer className="platform-pagination"><span>共 {total} 人，每页 {PAGE_SIZE} 人</span><div><button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>上一页</button><span>{page} / {pageCount}</span><button type="button" disabled={page >= pageCount} onClick={() => setPage(page + 1)}>下一页</button></div></footer>

    {detail && <div className="platform-modal-layer"><section className="platform-modal platform-personal-lawyer-detail" role="dialog" aria-modal="true" aria-label="个人律师账户管理">
      <header><div><h2>{detail.lawyer_name}</h2><p>{detail.lawyer_number} · {detail.mobile} · {detail.law_firm_name ?? "未填写律所"}</p></div><button type="button" aria-label="关闭" onClick={() => setDetail(null)}>×</button></header>
      <div className={`platform-lawyer-account-state is-${detail.account_status.toLowerCase()}`}><div><strong>{detail.account_status === "ACTIVE" ? "账号正常" : "账号已停用"}</strong><span>{detail.account_status === "ACTIVE" ? "当前可正常登录，实际产品权限以下方配置为准。" : "已禁止登录和使用服务。"}</span></div><button type="button" className={detail.account_status === "ACTIVE" ? "is-danger" : "is-positive"} onClick={() => setConfirmation({ enabled: detail.account_status !== "ACTIVE", reason: "" })}>{detail.account_status === "ACTIVE" ? "停用账号" : "恢复账号"}</button></div>
      <h3>余额管理</h3>
      <div className="platform-personal-balance-manager"><div><span>充值余额</span><strong>¥{Number(detail.recharge_balance).toFixed(2)}</strong></div><div><span>奖励余额</span><strong>¥{Number(detail.reward_balance).toFixed(2)}</strong></div><select aria-label="调整账户" value={balanceDraft.bucket} onChange={(event) => setBalanceDraft({ ...balanceDraft, bucket: event.target.value as "RECHARGE" | "REWARD" })}><option value="RECHARGE">充值余额</option><option value="REWARD">奖励余额</option></select><input aria-label="调整金额" value={balanceDraft.amount} onChange={(event) => setBalanceDraft({ ...balanceDraft, amount: event.target.value })} placeholder="增加填正数，扣减填负数" inputMode="decimal" /><input aria-label="调整原因" value={balanceDraft.reason} onChange={(event) => setBalanceDraft({ ...balanceDraft, reason: event.target.value })} placeholder="填写调整原因" /><button type="button" disabled={submitting || !balanceDraft.amount.trim() || !balanceDraft.reason.trim()} onClick={() => void adjustBalance()}>确认调整</button></div>
      <h3>产品权限与个人价格</h3>
      <div className="platform-personal-product-list">{detail.products.map((product) => {
        const draft = productDrafts[product.product_code] ?? { status: product.status, price: product.personal_price ?? "" };
        return <div key={product.product_code}><span><strong>{product.product_name}</strong><small>{product.module_name}</small></span><select aria-label={`${product.product_name}权限`} value={draft.status} onChange={(event) => setProductDrafts({ ...productDrafts, [product.product_code]: { ...draft, status: event.target.value as InstitutionProductStatus } })}><option value="HIDDEN">不展示</option><option value="VISIBLE_DISABLED">展示但不可用</option><option value="OPEN">已开通</option></select><input aria-label={`${product.product_name}个人价格`} value={draft.price} onChange={(event) => setProductDrafts({ ...productDrafts, [product.product_code]: { ...draft, price: event.target.value } })} placeholder="个人价格" inputMode="decimal" /><button type="button" disabled={submitting} onClick={() => void saveProduct(product.product_code)}>保存</button></div>;
      })}</div>
      <h3>操作记录</h3><ul className="platform-admin-audit-list">{detail.audits.length === 0 ? <li>暂无操作记录</li> : detail.audits.map((audit, index) => <li key={`${audit.created_at}-${index}`}><strong>{audit.action}</strong><span>{formatChinaDateTime(audit.created_at)} · {audit.operator_name}</span></li>)}</ul>
    </section></div>}

    {confirmation && <div className="platform-modal-layer"><section className="platform-modal platform-confirm-modal" role="dialog" aria-modal="true" aria-label={confirmation.enabled ? "恢复账号" : "停用账号"}><h2>{confirmation.enabled ? "恢复账号" : "停用账号"}</h2><p>{confirmation.enabled ? "恢复后，律师可重新登录。" : "停用后会立即撤销该律师的现有登录会话。"}</p><label>操作原因<textarea value={confirmation.reason} onChange={(event) => setConfirmation({ ...confirmation, reason: event.target.value })} placeholder="请填写操作原因" /></label><div className="platform-modal-actions"><button className="is-secondary" type="button" onClick={() => setConfirmation(null)}>取消</button><button className="platform-primary-button" type="button" disabled={!confirmation.reason.trim() || submitting} onClick={() => void changeStatus()}>确认</button></div></section></div>}
  </section>;
}
